import * as THREE from 'three';
import { TrackPoint, MapTheme, HazardDef, RAMP_HEIGHT, RAMP_LENGTH } from '@shared/types';
import { HazardState } from '@shared/hazards';
import { MapDef } from '@shared/maps';

/** Runtime state for a single hazard mesh */
interface HazardMeshEntry {
  def: HazardDef;
  group: THREE.Group;
  /** For laser gates: the beam mesh toggled on/off */
  beam?: THREE.Mesh;
  /** Base world position on the track */
  basePosition: THREE.Vector3;
  /** Track tangent at base position */
  tangent: THREE.Vector3;
  /** Track normal at base position */
  normal: THREE.Vector3;
  /** Road half-width for lateral offset computation */
  halfWidth: number;
}

// Typed container for the internal Three.js shader object populated by onBeforeCompile
interface CompiledShader {
  uniforms: Record<string, THREE.IUniform>;
}

export class TrackRenderer {
  private roadMesh: THREE.Mesh | null = null;
  private stripeMesh: THREE.Mesh | null = null;
  private wallMeshes: THREE.Mesh[] = [];
  private groundMesh: THREE.Mesh | null = null;
  private startLineMesh: THREE.Group | null = null;
  private decorations: THREE.Object3D[] = [];
  private tileGroup: THREE.Group | null = null;
  private hazardEntries: HazardMeshEntry[] = [];

  // Material references that hold animated shaders
  private roadMat: THREE.MeshStandardMaterial | null = null;
  private spaceWallMats: THREE.MeshStandardMaterial[] = [];
  private groundMat: THREE.MeshStandardMaterial | null = null;

  constructor(private scene: THREE.Scene) {}

  /** Build track geometry from expanded path points and map definition */
  build(path: TrackPoint[], mapDef: MapDef): void {
    this.clear();

    this.buildRoad(path, mapDef.roadWidth, mapDef.theme, mapDef.id);
    this.buildWalls(path, mapDef.roadWidth, mapDef.wallHeight, mapDef.theme, mapDef.id);
    this.buildGround(mapDef.theme, mapDef.id);
    this.buildStartLine(path[0], path[1], mapDef.roadWidth);
    this.buildDecorations(path, mapDef);
    this.buildHazardMeshes(path, mapDef);
  }

  // ────────────────────────────────────────────────────────────
  // Road
  // ────────────────────────────────────────────────────────────

  private buildRoad(path: TrackPoint[], width: number, theme: MapTheme, mapId: string): void {
    const n = path.length;
    const halfWidth = width / 2;

    // Positions: 2 vertices per path point (left, right), 3 components each
    const positions = new Float32Array(n * 2 * 3);
    const uvs = new Float32Array(n * 2 * 2);
    const indices: number[] = [];

    let cumulativeLen = 0;
    for (let i = 0; i < n; i++) {
      const p = path[i];

      // Accumulated distance for UV tiling
      if (i > 0) {
        const prev = path[i - 1];
        const dx = p.x - prev.x;
        const dz = p.z - prev.z;
        cumulativeLen += Math.sqrt(dx * dx + dz * dz);
      }

      const li = i * 2; // left vertex index
      const ri = i * 2 + 1; // right vertex index

      // Left vertex: center - normal * halfWidth
      positions[li * 3 + 0] = p.x - p.nx * halfWidth;
      positions[li * 3 + 1] = 0.01; // slightly above ground
      positions[li * 3 + 2] = p.z - p.nz * halfWidth;

      // Right vertex: center + normal * halfWidth
      positions[ri * 3 + 0] = p.x + p.nx * halfWidth;
      positions[ri * 3 + 1] = 0.01;
      positions[ri * 3 + 2] = p.z + p.nz * halfWidth;

      // UVs: u goes 0..1 across road, v tiles along length
      uvs[li * 2 + 0] = 0;
      uvs[li * 2 + 1] = cumulativeLen / 5; // tile every 5 units
      uvs[ri * 2 + 0] = 1;
      uvs[ri * 2 + 1] = cumulativeLen / 5;

      // Create two triangles connecting this pair to the next pair
      if (i < n - 1) {
        const nextLi = (i + 1) * 2;
        const nextRi = (i + 1) * 2 + 1;
        indices.push(li, nextLi, ri);
        indices.push(ri, nextLi, nextRi);
      }
    }

    // Close the loop: connect last pair to first pair
    const lastL = (n - 1) * 2;
    const lastR = (n - 1) * 2 + 1;
    indices.push(lastL, 0, lastR);
    indices.push(lastR, 0, 1);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    const material = this.createRoadMaterial(theme, mapId);
    this.roadMat = material;

    this.roadMesh = new THREE.Mesh(geometry, material);
    this.roadMesh.receiveShadow = true;
    this.scene.add(this.roadMesh);

    // Center stripe
    this.buildCenterStripe(path, theme);
  }

  // ────────────────────────────────────────────────────────────
  // Road material factory (onBeforeCompile injects custom GLSL into
  // MeshStandardMaterial so shadows, fog and PBR lighting are preserved)
  // ────────────────────────────────────────────────────────────

  private createRoadMaterial(
    theme: MapTheme,
    mapId: string
  ): THREE.MeshStandardMaterial {
    const stripeColor = new THREE.Color(theme.roadStripeColor);

    // Shared noise helper injected into every variant
    const noiseFunctions = /* glsl */`
      float roadHash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }
      float roadNoise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(
          mix(roadHash(i),             roadHash(i + vec2(1.0, 0.0)), f.x),
          mix(roadHash(i + vec2(0.0,1.0)), roadHash(i + vec2(1.0, 1.0)), f.x),
          f.y
        );
      }
    `;

    // Shared vertex additions — expose vMyUv to the fragment shader
    const uvVaryingDecl = /* glsl */`
      #include <uv_pars_vertex>
      varying vec2 vMyUv;
    `;
    const uvVaryingAssign = /* glsl */`
      #include <uv_vertex>
      vMyUv = uv;
    `;

    // ── Space Station: dark surface + animated neon grid ──
    if (mapId === 'space_station') {
      const mat = new THREE.MeshStandardMaterial({
        color: theme.roadColor,
        roughness: 0.5,
        metalness: 0.2,
        side: THREE.DoubleSide,
      });

      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uTime        = { value: 0 };
        shader.uniforms.uStripeColor = { value: stripeColor };

        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_pars_vertex>',
          uvVaryingDecl
        );
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_vertex>',
          uvVaryingAssign
        );

        // Inject uniform + noise declarations before roughnessFactor
        shader.fragmentShader = shader.fragmentShader.replace(
          'uniform float roughnessFactor;',
          /* glsl */`uniform float roughnessFactor;
          uniform float uTime;
          uniform vec3 uStripeColor;
          varying vec2 vMyUv;
          ${noiseFunctions}`
        );

        // Override diffuse color with neon grid effect
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <map_fragment>',
          /* glsl */`#include <map_fragment>

          // Neon grid lines — 6 lanes across, dense grid along track
          float gridU = fract(vMyUv.x * 6.0);
          float gridV = fract(vMyUv.y * 12.0);
          float lineU = 1.0 - smoothstep(0.0, 0.04, min(gridU, 1.0 - gridU));
          float lineV = 1.0 - smoothstep(0.0, 0.02, min(gridV, 1.0 - gridV));
          float grid = max(lineU, lineV);

          // Animated flow — grid lines scroll forward along track
          float flowV = fract(vMyUv.y * 12.0 - uTime * 0.3);
          float flowLine = 1.0 - smoothstep(0.0, 0.015, flowV);
          float flow = flowLine * 0.4;

          vec3 neonColor = uStripeColor; // blue-purple from theme
          diffuseColor.rgb = mix(diffuseColor.rgb, neonColor, grid * 0.7 + flow);
          diffuseColor.rgb += neonColor * grid * 0.25; // additive glow
          `
        );

        (mat as THREE.MeshStandardMaterial & { _shader?: CompiledShader })._shader = shader;
      };

      return mat;
    }

    // ── Ice World: caustic shimmer + subsurface scatter ──
    if (mapId === 'ice_world') {
      const mat = new THREE.MeshStandardMaterial({
        color: theme.roadColor,
        roughness: 0.1,
        metalness: 0.05,
        side: THREE.DoubleSide,
      });

      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uTime        = { value: 0 };
        shader.uniforms.uStripeColor = { value: stripeColor };

        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_pars_vertex>',
          uvVaryingDecl
        );
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_vertex>',
          uvVaryingAssign
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          'uniform float roughnessFactor;',
          /* glsl */`uniform float roughnessFactor;
          uniform float uTime;
          uniform vec3 uStripeColor;
          varying vec2 vMyUv;
          ${noiseFunctions}`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <map_fragment>',
          /* glsl */`#include <map_fragment>

          // Caustic shimmer from two offset noise layers
          float shimmer = roadNoise(vMyUv * 15.0 + uTime * 0.05) * 0.15;
          float caustic = roadNoise(vMyUv * 30.0 - vec2(uTime * 0.08, 0.0)) * 0.08;
          diffuseColor.rgb += vec3(shimmer + caustic);

          // Subsurface scatter: bright blue tinge from "below" the ice at center
          float scatter = pow(1.0 - abs(vMyUv.x - 0.5) * 2.0, 3.0) * 0.1;
          diffuseColor.rgb += vec3(0.3, 0.5, 0.8) * scatter;

          // Edge highlight stripes (frozen lane markers)
          float uCenter = abs(vMyUv.x - 0.5);
          float dashV   = fract(vMyUv.y * 8.0);
          float dashMask = step(0.3, dashV) * step(dashV, 0.7);
          float centerLine = (1.0 - smoothstep(0.01, 0.025, uCenter)) * dashMask;
          float leftEdge  = 1.0 - smoothstep(0.0,   0.012, vMyUv.x);
          float rightEdge = 1.0 - smoothstep(0.988, 1.0,   vMyUv.x);
          float edgeLine  = max(leftEdge, rightEdge) * 0.4;
          diffuseColor.rgb = mix(diffuseColor.rgb, uStripeColor, centerLine * 0.7 + edgeLine);
          `
        );

        (mat as THREE.MeshStandardMaterial & { _shader?: CompiledShader })._shader = shader;
      };

      return mat;
    }

    // ── Desert Canyon: sandy grain + wind ripples ──
    if (mapId === 'desert_canyon') {
      const mat = new THREE.MeshStandardMaterial({
        color: theme.roadColor,
        roughness: 0.9,
        metalness: 0.0,
        side: THREE.DoubleSide,
      });

      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uTime        = { value: 0 };
        shader.uniforms.uStripeColor = { value: stripeColor };

        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_pars_vertex>',
          uvVaryingDecl
        );
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_vertex>',
          uvVaryingAssign
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          'uniform float roughnessFactor;',
          /* glsl */`uniform float roughnessFactor;
          uniform float uTime;
          uniform vec3 uStripeColor;
          varying vec2 vMyUv;
          ${noiseFunctions}`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <map_fragment>',
          /* glsl */`#include <map_fragment>

          // Multi-scale sand grain
          float sand = roadNoise(vMyUv * 40.0) * 0.12
                     + roadNoise(vMyUv * 80.0 + 0.5) * 0.05;
          // Animated wind ripples — slow-moving fine corrugations
          float windRipple = roadNoise(vec2(vMyUv.x * 3.0,
                                           vMyUv.y * 60.0 + uTime * 0.02)) * 0.06;
          diffuseColor.rgb *= (0.9 + sand + windRipple);

          // Subtle tyre-track stripe at center
          float uCenter  = abs(vMyUv.x - 0.5);
          float dashV    = fract(vMyUv.y * 6.0);
          float dashMask = step(0.3, dashV) * step(dashV, 0.7);
          float centerLine = (1.0 - smoothstep(0.01, 0.025, uCenter)) * dashMask;
          float leftEdge  = 1.0 - smoothstep(0.0,   0.012, vMyUv.x);
          float rightEdge = 1.0 - smoothstep(0.988, 1.0,   vMyUv.x);
          float edgeLine  = max(leftEdge, rightEdge) * 0.3;
          diffuseColor.rgb = mix(diffuseColor.rgb, uStripeColor, centerLine * 0.5 + edgeLine);
          `
        );

        (mat as THREE.MeshStandardMaterial & { _shader?: CompiledShader })._shader = shader;
      };

      return mat;
    }

    // ── Haunted Mansion: dark stone + procedural cracks ──
    if (mapId === 'haunted_mansion') {
      const mat = new THREE.MeshStandardMaterial({
        color: theme.roadColor,
        roughness: 0.9,
        metalness: 0.05,
        side: THREE.DoubleSide,
      });

      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uTime        = { value: 0 };
        shader.uniforms.uStripeColor = { value: stripeColor };

        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_pars_vertex>',
          uvVaryingDecl
        );
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_vertex>',
          uvVaryingAssign
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          'uniform float roughnessFactor;',
          /* glsl */`uniform float roughnessFactor;
          uniform float uTime;
          uniform vec3 uStripeColor;
          varying vec2 vMyUv;
          ${noiseFunctions}`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <map_fragment>',
          /* glsl */`#include <map_fragment>

          // Stone age variation
          float age = roadNoise(vMyUv * 10.0) * 0.1;
          // Crack pattern: thresholded high-frequency noise
          float crack = smoothstep(0.95, 1.0, roadNoise(vMyUv * 25.0));
          diffuseColor.rgb *= (0.85 + age);
          diffuseColor.rgb -= vec3(crack * 0.15);
          // Purple tinge seeping through the cracks
          diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.3, 0.1, 0.4), crack * 0.4);

          // Ghost-light center stripe (very subtle, no dashes — eerie solid line)
          float uCenter = abs(vMyUv.x - 0.5);
          float centerLine = (1.0 - smoothstep(0.01, 0.025, uCenter));
          float leftEdge  = 1.0 - smoothstep(0.0,   0.012, vMyUv.x);
          float rightEdge = 1.0 - smoothstep(0.988, 1.0,   vMyUv.x);
          float edgeLine  = max(leftEdge, rightEdge) * 0.35;
          diffuseColor.rgb = mix(diffuseColor.rgb, uStripeColor, centerLine * 0.4 + edgeLine);
          `
        );

        (mat as THREE.MeshStandardMaterial & { _shader?: CompiledShader })._shader = shader;
      };

      return mat;
    }

    // ── Default (Candy World, Giant Bathroom, others): asphalt grain + dashes ──
    const mat = new THREE.MeshStandardMaterial({
      color: theme.roadColor,
      roughness: 0.8,
      metalness: 0.0,
      side: THREE.DoubleSide,
    });

    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime        = { value: 0 };
      shader.uniforms.uStripeColor = { value: stripeColor };

      shader.vertexShader = shader.vertexShader.replace(
        '#include <uv_pars_vertex>',
        uvVaryingDecl
      );
      shader.vertexShader = shader.vertexShader.replace(
        '#include <uv_vertex>',
        uvVaryingAssign
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        'uniform float roughnessFactor;',
        /* glsl */`uniform float roughnessFactor;
        uniform float uTime;
        uniform vec3 uStripeColor;
        varying vec2 vMyUv;
        ${noiseFunctions}`
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <map_fragment>',
        /* glsl */`#include <map_fragment>

        // Subtle asphalt grain
        float grain = roadNoise(vMyUv * 80.0) * 0.06;
        diffuseColor.rgb *= (0.97 + grain);

        // Dashed center line — 8 dashes per UV-tile along track
        float uCenter  = abs(vMyUv.x - 0.5);
        float dashV    = fract(vMyUv.y * 8.0);
        float dashMask = step(0.3, dashV) * step(dashV, 0.7);
        float centerLine = (1.0 - smoothstep(0.01, 0.025, uCenter)) * dashMask;

        // Edge highlight lines (lane boundary)
        float leftEdge  = 1.0 - smoothstep(0.0,   0.012, vMyUv.x);
        float rightEdge = 1.0 - smoothstep(0.988, 1.0,   vMyUv.x);
        float edgeLine  = max(leftEdge, rightEdge) * 0.4;

        diffuseColor.rgb = mix(diffuseColor.rgb, uStripeColor, centerLine * 0.9 + edgeLine);
        `
      );

      (mat as THREE.MeshStandardMaterial & { _shader?: CompiledShader })._shader = shader;
    };

    return mat;
  }

  private buildCenterStripe(path: TrackPoint[], theme: MapTheme): void {
    const n = path.length;
    const stripeHalf = 0.3;
    const positions = new Float32Array(n * 2 * 3);
    const indices: number[] = [];

    // Dashed stripe: alternate visible/invisible segments
    const dashLength = 3; // in path points
    let cumulativeLen = 0;

    for (let i = 0; i < n; i++) {
      const p = path[i];

      if (i > 0) {
        const prev = path[i - 1];
        const dx = p.x - prev.x;
        const dz = p.z - prev.z;
        cumulativeLen += Math.sqrt(dx * dx + dz * dz);
      }

      const li = i * 2;
      const ri = i * 2 + 1;

      positions[li * 3 + 0] = p.x - p.nx * stripeHalf;
      positions[li * 3 + 1] = 0.02;
      positions[li * 3 + 2] = p.z - p.nz * stripeHalf;

      positions[ri * 3 + 0] = p.x + p.nx * stripeHalf;
      positions[ri * 3 + 1] = 0.02;
      positions[ri * 3 + 2] = p.z + p.nz * stripeHalf;

      // Only create faces for "on" segments of the dashed line
      const segment = Math.floor(i / dashLength);
      if (segment % 2 === 0 && i < n - 1) {
        const nextLi = (i + 1) * 2;
        const nextRi = (i + 1) * 2 + 1;
        indices.push(li, nextLi, ri);
        indices.push(ri, nextLi, nextRi);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    const material = new THREE.MeshStandardMaterial({
      color: theme.roadStripeColor,
      roughness: 0.7,
      metalness: 0.0,
      side: THREE.DoubleSide,
    });

    this.stripeMesh = new THREE.Mesh(geometry, material);
    this.stripeMesh.receiveShadow = true;
    this.scene.add(this.stripeMesh);
  }

  // ────────────────────────────────────────────────────────────
  // Walls
  // ────────────────────────────────────────────────────────────

  private buildWalls(
    path: TrackPoint[],
    width: number,
    height: number,
    theme: MapTheme,
    mapId: string
  ): void {
    this.buildWallSide(path, width, height, theme, mapId, -1); // left
    this.buildWallSide(path, width, height, theme, mapId, 1); // right
  }

  private buildWallSide(
    path: TrackPoint[],
    width: number,
    height: number,
    theme: MapTheme,
    mapId: string,
    side: number // -1 left, +1 right
  ): void {
    const n = path.length;
    const halfWidth = width / 2;

    // 2 verts per path point: bottom and top
    const positions = new Float32Array(n * 2 * 3);
    // UVs: u tracks 0→1 vertically (bottom→top), v tiles along the wall length
    const uvs = new Float32Array(n * 2 * 2);
    const indices: number[] = [];

    let cumulativeLen = 0;
    for (let i = 0; i < n; i++) {
      const p = path[i];
      const baseX = p.x + p.nx * halfWidth * side;
      const baseZ = p.z + p.nz * halfWidth * side;

      if (i > 0) {
        const prev = path[i - 1];
        const dx = baseX - (prev.x + prev.nx * halfWidth * side);
        const dz = baseZ - (prev.z + prev.nz * halfWidth * side);
        cumulativeLen += Math.sqrt(dx * dx + dz * dz);
      }

      const bi = i * 2; // bottom vertex
      const ti = i * 2 + 1; // top vertex

      positions[bi * 3 + 0] = baseX;
      positions[bi * 3 + 1] = 0;
      positions[bi * 3 + 2] = baseZ;

      positions[ti * 3 + 0] = baseX;
      positions[ti * 3 + 1] = height;
      positions[ti * 3 + 2] = baseZ;

      // UV: u=0 at bottom, u=1 at top; v tiles every 5 world-units along length
      uvs[bi * 2 + 0] = 0.0;
      uvs[bi * 2 + 1] = cumulativeLen / 5;
      uvs[ti * 2 + 0] = 1.0;
      uvs[ti * 2 + 1] = cumulativeLen / 5;

      if (i < n - 1) {
        const nextBi = (i + 1) * 2;
        const nextTi = (i + 1) * 2 + 1;

        if (side === -1) {
          // Left wall: face inward (toward right, i.e. toward +normal)
          indices.push(bi, nextBi, ti);
          indices.push(ti, nextBi, nextTi);
        } else {
          // Right wall: face inward (toward left, i.e. toward -normal)
          indices.push(bi, ti, nextBi);
          indices.push(ti, nextTi, nextBi);
        }
      }
    }

    // Close the loop
    const lastBi = (n - 1) * 2;
    const lastTi = (n - 1) * 2 + 1;
    if (side === -1) {
      indices.push(lastBi, 0, lastTi);
      indices.push(lastTi, 0, 1);
    } else {
      indices.push(lastBi, lastTi, 0);
      indices.push(lastTi, 1, 0);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    const material = this.createWallMaterial(theme, mapId);

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.wallMeshes.push(mesh);
    this.scene.add(mesh);
  }

  // ────────────────────────────────────────────────────────────
  // Wall material factory
  // ────────────────────────────────────────────────────────────

  private createWallMaterial(
    theme: MapTheme,
    mapId: string
  ): THREE.MeshStandardMaterial {
    // ── Space Station: pulsing neon strips on dark wall panels ──
    if (mapId === 'space_station') {
      const mat = new THREE.MeshStandardMaterial({
        color: theme.wallColor,
        roughness: 0.4,
        metalness: 0.5,
        side: THREE.DoubleSide,
      });

      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uTime      = { value: 0 };
        shader.uniforms.uWallColor = { value: new THREE.Color(theme.wallColor) };
        shader.uniforms.uNeonColor = { value: new THREE.Color(0.2, 0.4, 1.0) };

        // Expose uv as a custom varying (same pattern as road)
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_pars_vertex>',
          /* glsl */`#include <uv_pars_vertex>
          varying vec2 vWallUv;`
        );
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_vertex>',
          /* glsl */`#include <uv_vertex>
          vWallUv = uv;`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          'uniform float roughnessFactor;',
          /* glsl */`uniform float roughnessFactor;
          uniform float uTime;
          uniform vec3 uWallColor;
          uniform vec3 uNeonColor;
          varying vec2 vWallUv;`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <map_fragment>',
          /* glsl */`#include <map_fragment>

          // Horizontal neon strip repeating every 1/20th of the wall height
          float stripV = fract(vWallUv.x * 20.0); // vWallUv.x = 0 bottom, 1 top
          float strip  = 1.0 - smoothstep(0.0, 0.06, min(stripV, 1.0 - stripV));

          // Pulse — each strip has a phase offset so they don't all blink together
          float phase  = vWallUv.x * 10.0;
          float pulse  = 1.5 + 0.5 * sin(uTime * 2.0 + phase + vWallUv.y * 5.0);

          vec3 neon    = uNeonColor * strip * pulse;
          diffuseColor.rgb += neon;
          `
        );

        (mat as THREE.MeshStandardMaterial & { _shader?: CompiledShader })._shader = shader;
        this.spaceWallMats.push(mat);
      };

      return mat;
    }

    // ── Default wall material (no animated shader) ──
    return new THREE.MeshStandardMaterial({
      color: theme.wallColor,
      roughness: 0.6,
      metalness: 0.1,
      side: THREE.DoubleSide,
    });
  }

  // ────────────────────────────────────────────────────────────
  // Ground
  // ────────────────────────────────────────────────────────────

  private buildGround(theme: MapTheme, mapId: string): void {
    const geometry = new THREE.PlaneGeometry(400, 400, 1, 1);
    geometry.rotateX(-Math.PI / 2);

    const material = this.createGroundMaterial(theme, mapId);
    this.groundMat = material;

    this.groundMesh = new THREE.Mesh(geometry, material);
    this.groundMesh.position.y = -0.05;
    this.groundMesh.receiveShadow = true;
    this.scene.add(this.groundMesh);
  }

  // ────────────────────────────────────────────────────────────
  // Ground material factory
  // ────────────────────────────────────────────────────────────

  private createGroundMaterial(
    theme: MapTheme,
    mapId: string
  ): THREE.MeshStandardMaterial {
    const noiseFunctions = /* glsl */`
      float gHash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }
      float gNoise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(
          mix(gHash(i),             gHash(i + vec2(1.0, 0.0)), f.x),
          mix(gHash(i + vec2(0.0, 1.0)), gHash(i + vec2(1.0, 1.0)), f.x),
          f.y
        );
      }
    `;

    // ── Space Station: dark starfield ──
    if (mapId === 'space_station') {
      const mat = new THREE.MeshStandardMaterial({
        color: theme.groundColor,
        roughness: 1.0,
        metalness: 0.0,
      });

      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uTime = { value: 0 };

        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_pars_vertex>',
          /* glsl */`#include <uv_pars_vertex>
          varying vec2 vGroundUv;`
        );
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_vertex>',
          /* glsl */`#include <uv_vertex>
          vGroundUv = uv;`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          'uniform float roughnessFactor;',
          /* glsl */`uniform float roughnessFactor;
          uniform float uTime;
          varying vec2 vGroundUv;
          ${noiseFunctions}`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <map_fragment>',
          /* glsl */`#include <map_fragment>

          // Starfield: tiny bright points scattered across the plane
          vec2 stGrid = floor(vGroundUv * 200.0);
          float starRand = gHash(stGrid);
          float starBright = step(0.985, starRand); // ~1.5% coverage
          // Subtle twinkle per star using its unique hash as phase
          float twinkle = 0.6 + 0.4 * sin(uTime * 2.5 + starRand * 6.283);
          diffuseColor.rgb += vec3(starBright * twinkle);

          // Large-scale nebula colour wash
          float nebula = gNoise(vGroundUv * 4.0 + uTime * 0.005) * 0.06;
          diffuseColor.rgb += vec3(0.1, 0.05, 0.3) * nebula;
          `
        );

        (mat as THREE.MeshStandardMaterial & { _shader?: CompiledShader })._shader = shader;
      };

      return mat;
    }

    // ── Desert Canyon: sand dune gradient with heat shimmer ──
    if (mapId === 'desert_canyon') {
      const mat = new THREE.MeshStandardMaterial({
        color: theme.groundColor,
        roughness: 1.0,
        metalness: 0.0,
      });

      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uTime = { value: 0 };

        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_pars_vertex>',
          /* glsl */`#include <uv_pars_vertex>
          varying vec2 vGroundUv;`
        );
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_vertex>',
          /* glsl */`#include <uv_vertex>
          vGroundUv = uv;`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          'uniform float roughnessFactor;',
          /* glsl */`uniform float roughnessFactor;
          uniform float uTime;
          varying vec2 vGroundUv;
          ${noiseFunctions}`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <map_fragment>',
          /* glsl */`#include <map_fragment>

          // Dune ripples — large-scale smooth noise
          float dune = gNoise(vGroundUv * 6.0) * 0.15
                     + gNoise(vGroundUv * 14.0) * 0.06;
          // Sandy colour: shift toward orange at dune crests
          vec3 sandTint = vec3(0.12, 0.04, -0.04) * dune;
          diffuseColor.rgb += sandTint;

          // Fine grain
          float grain = gNoise(vGroundUv * 120.0) * 0.04;
          diffuseColor.rgb *= (0.96 + grain);
          `
        );

        (mat as THREE.MeshStandardMaterial & { _shader?: CompiledShader })._shader = shader;
      };

      return mat;
    }

    // ── Default ground (plain, no animated shader) ──
    return new THREE.MeshStandardMaterial({
      color: theme.groundColor,
      roughness: 0.9,
      metalness: 0.0,
    });
  }

  // ────────────────────────────────────────────────────────────
  // Start / Finish Line
  // ────────────────────────────────────────────────────────────

  private buildStartLine(p0: TrackPoint, p1: TrackPoint, width: number): void {
    const group = new THREE.Group();
    const halfWidth = width / 2;
    const lineDepth = 2.0; // how far along the track the start line extends

    // Checkered pattern: 8 squares across, 2 rows deep
    const squaresAcross = 8;
    const squareSize = width / squaresAcross;
    const rows = 2;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < squaresAcross; col++) {
        const isWhite = (row + col) % 2 === 0;
        const geo = new THREE.PlaneGeometry(squareSize, lineDepth / rows);
        geo.rotateX(-Math.PI / 2);

        const mat = new THREE.MeshStandardMaterial({
          color: isWhite ? 0xffffff : 0x111111,
          roughness: 0.5,
          metalness: 0.0,
        });

        const tile = new THREE.Mesh(geo, mat);

        // Position each tile relative to the track center line
        // Lateral position: from -halfWidth to +halfWidth
        const lateralT = (col + 0.5) / squaresAcross; // 0..1
        const lateralOffset = -halfWidth + lateralT * width;
        // Along-track position
        const alongOffset = (row + 0.5) * (lineDepth / rows) - lineDepth / 2;

        tile.position.set(
          p0.x + p0.nx * lateralOffset + p0.dx * alongOffset,
          0.03, // just above road
          p0.z + p0.nz * lateralOffset + p0.dz * alongOffset
        );

        // Orient tile to align with the track direction
        tile.rotation.y = -Math.atan2(p0.dz, p0.dx);

        group.add(tile);
      }
    }

    this.startLineMesh = group;
    this.scene.add(group);
  }

  // ────────────────────────────────────────────────────────────
  // Decorations Router
  // ────────────────────────────────────────────────────────────

  private buildDecorations(path: TrackPoint[], mapDef: MapDef): void {
    if (mapDef.id === 'candy_world') {
      this.buildCandyDecorations(path, mapDef);
    } else if (mapDef.id === 'giant_bathroom') {
      this.buildBathroomDecorations(path, mapDef);
    } else if (mapDef.id === 'desert_canyon') {
      this.buildDesertCanyonDecorations(path, mapDef);
    } else if (mapDef.id === 'space_station') {
      this.buildSpaceStationDecorations(path, mapDef);
    } else if (mapDef.id === 'haunted_mansion') {
      this.buildHauntedMansionDecorations(path, mapDef);
    } else if (mapDef.id === 'ice_world') {
      this.buildIceWorldDecorations(path, mapDef);
    }
    // Build ramps on all maps
    this.buildRamps(path, mapDef);
  }

  // ────────────────────────────────────────────────────────────
  // Ramps (both maps)
  // ────────────────────────────────────────────────────────────

  private buildRamps(path: TrackPoint[], mapDef: MapDef): void {
    const fractions = mapDef.rampPositions ?? [0.2, 0.5, 0.75];
    const n = path.length;
    const rampWidth = mapDef.roadWidth * 0.6;
    const rampLength = RAMP_LENGTH;
    const rampHeight = RAMP_HEIGHT;

    for (let ri = 0; ri < fractions.length; ri++) {
      const pathIdx = Math.floor(fractions[ri] * n) % n;
      const p = path[pathIdx];

      // Slight lateral offsets for variety: center, slightly left, slightly right
      const lateralOffsets = [0, -mapDef.roadWidth * 0.1, mapDef.roadWidth * 0.1];
      const lateralOffset = lateralOffsets[ri % lateralOffsets.length];

      const group = new THREE.Group();

      if (mapDef.id === 'candy_world') {
        this.buildCandyRamp(group, rampWidth, rampLength, rampHeight, ri);
      } else if (mapDef.id === 'desert_canyon') {
        this.buildDesertRamp(group, rampWidth, rampLength, rampHeight, ri);
      } else if (mapDef.id === 'space_station') {
        this.buildSpaceRamp(group, rampWidth, rampLength, rampHeight, ri);
      } else if (mapDef.id === 'haunted_mansion') {
        this.buildHauntedRamp(group, rampWidth, rampLength, rampHeight, ri);
      } else if (mapDef.id === 'ice_world') {
        this.buildIceRamp(group, rampWidth, rampLength, rampHeight, ri);
      } else {
        this.buildBathroomRamp(group, rampWidth, rampLength, rampHeight, ri);
      }

      // Position and orient along the track
      const angle = Math.atan2(p.dz, p.dx);
      group.position.set(
        p.x + p.nx * lateralOffset,
        0,
        p.z + p.nz * lateralOffset
      );
      group.rotation.y = -angle + Math.PI / 2;

      this.scene.add(group);
      this.decorations.push(group);
    }
  }

  /** Build a wedge-shaped ramp geometry (slope rises from front to back) */
  private buildRampWedge(width: number, length: number, height: number): THREE.BufferGeometry {
    // Wedge: 6 vertices
    // Bottom: 4 corners of a rectangle at y=0
    // Top: 2 corners at the back edge at y=height
    // Front edge (z = -length/2) is at y=0, back edge (z = +length/2) rises to height
    const hw = width / 2;
    const hl = length / 2;

    // Vertices:
    // 0: front-left  (-hw, 0, -hl)
    // 1: front-right ( hw, 0, -hl)
    // 2: back-right  ( hw, 0,  hl)  bottom
    // 3: back-left   (-hw, 0,  hl)  bottom
    // 4: back-left   (-hw, height, hl) top
    // 5: back-right  ( hw, height, hl) top

    const vertices = new Float32Array([
      -hw, 0, -hl,      //  0: front-left bottom
       hw, 0, -hl,      //  1: front-right bottom
       hw, 0,  hl,      //  2: back-right bottom
      -hw, 0,  hl,      //  3: back-left bottom
      -hw, height, hl,  //  4: back-left top
       hw, height, hl,  //  5: back-right top
    ]);

    // Faces (triangles, CCW winding for front-face):
    const indices = [
      // Slope face (front-left, front-right -> back-left-top, back-right-top)
      0, 1, 5,
      0, 5, 4,
      // Bottom face
      0, 2, 1,
      0, 3, 2,
      // Back face (vertical back wall)
      3, 5, 2,
      3, 4, 5,
      // Left face
      0, 4, 3,
      // Right face
      1, 2, 5,
    ];

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }

  /** Candy World ramp: rainbow-striped candy wedge */
  private buildCandyRamp(
    group: THREE.Group,
    width: number,
    length: number,
    height: number,
    index: number
  ): void {
    const stripeCount = 5;
    const stripeWidth = width / stripeCount;
    const rainbowColors = [0xff69b4, 0xff3366, 0xffdd00, 0x44cc44, 0xcc44ff];

    for (let s = 0; s < stripeCount; s++) {
      const stripeGeo = this.buildRampWedge(stripeWidth, length, height);
      const stripeMat = new THREE.MeshStandardMaterial({
        color: rainbowColors[s % rainbowColors.length],
        roughness: 0.4,
        metalness: 0.1,
      });
      const stripe = new THREE.Mesh(stripeGeo, stripeMat);
      // Offset each stripe along the local X axis
      stripe.position.x = -width / 2 + stripeWidth / 2 + s * stripeWidth;
      stripe.position.y = 0.02; // just above road
      stripe.castShadow = false;
      stripe.receiveShadow = true;
      group.add(stripe);
    }

    // Add a glossy candy coating edge (thin border on top surface)
    const edgeGeo = new THREE.BoxGeometry(width + 0.2, 0.08, length + 0.2);
    const edgeMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.2,
      metalness: 0.3,
      transparent: true,
      opacity: 0.5,
    });
    const edge = new THREE.Mesh(edgeGeo, edgeMat);
    edge.position.y = 0.01;
    group.add(edge);
  }

  /** Bathroom ramp: giant bar soap / tile edge wedge */
  private buildBathroomRamp(
    group: THREE.Group,
    width: number,
    length: number,
    height: number,
    index: number
  ): void {
    const soapColors = [0xaaddff, 0xbbffbb, 0xddbbff];
    const mainColor = soapColors[index % soapColors.length];

    // Main wedge body
    const wedgeGeo = this.buildRampWedge(width, length, height);
    const wedgeMat = new THREE.MeshStandardMaterial({
      color: mainColor,
      roughness: 0.3,
      metalness: 0.05,
    });
    const wedge = new THREE.Mesh(wedgeGeo, wedgeMat);
    wedge.position.y = 0.02;
    wedge.castShadow = false;
    wedge.receiveShadow = true;
    group.add(wedge);

    // White label stripe on top (flat box along the slope center)
    const labelGeo = new THREE.BoxGeometry(width * 0.5, 0.06, length * 0.4);
    const labelMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.4,
      metalness: 0.0,
    });
    const label = new THREE.Mesh(labelGeo, labelMat);
    label.position.set(0, height * 0.5 + 0.05, length * 0.1);
    // Tilt the label to follow the slope
    label.rotation.x = -Math.atan2(height, length);
    group.add(label);

    // Small foam bubbles on the soap ramp
    for (let b = 0; b < 4; b++) {
      const bubbleGeo = new THREE.SphereGeometry(0.15 + Math.random() * 0.1, 8, 6);
      const bubbleMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.1,
        metalness: 0.3,
        transparent: true,
        opacity: 0.6,
      });
      const bubble = new THREE.Mesh(bubbleGeo, bubbleMat);
      bubble.position.set(
        (Math.random() - 0.5) * width * 0.8,
        height * 0.3 + Math.random() * height * 0.4,
        (Math.random() - 0.5) * length * 0.6
      );
      group.add(bubble);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Candy World Decorations
  // ────────────────────────────────────────────────────────────

  private buildCandyDecorations(path: TrackPoint[], mapDef: MapDef): void {
    const halfWidth = mapDef.roadWidth / 2;
    const n = path.length;

    const candyColors = [0xff69b4, 0xff3366, 0x44cc44, 0xffdd00, 0xcc44ff, 0xff6644, 0x44ddff];
    const pickColor = () => candyColors[Math.floor(Math.random() * candyColors.length)];

    // ---- 1. Lollipop Trees ----
    // Place 20 lollipops outside the track, alternating left/right
    for (let i = 0; i < 20; i++) {
      const pathIdx = Math.floor((i / 20) * n) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 5 + Math.random() * 8;

      const group = new THREE.Group();

      // Stick
      const stickGeo = new THREE.CylinderGeometry(0.25, 0.3, 6, 8);
      const stickMat = new THREE.MeshStandardMaterial({
        color: 0x8B5A2B,
        roughness: 0.9,
        metalness: 0.0,
      });
      const stick = new THREE.Mesh(stickGeo, stickMat);
      stick.position.y = 3;
      stick.castShadow = false;
      group.add(stick);

      // Candy top sphere
      const candyGeo = new THREE.SphereGeometry(2, 16, 12);
      const candyMat = new THREE.MeshStandardMaterial({
        color: pickColor(),
        roughness: 0.4,
        metalness: 0.1,
      });
      const candy = new THREE.Mesh(candyGeo, candyMat);
      candy.position.y = 7;
      candy.castShadow = false;
      group.add(candy);

      // Swirl decoration: a thin torus on the candy
      const swirlGeo = new THREE.TorusGeometry(1.4, 0.15, 8, 24);
      const swirlMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.5,
        metalness: 0.0,
      });
      const swirl = new THREE.Mesh(swirlGeo, swirlMat);
      swirl.position.y = 7;
      swirl.rotation.x = Math.random() * Math.PI;
      swirl.rotation.z = Math.random() * Math.PI;
      group.add(swirl);

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 2. Gumdrop Hills ----
    // Place 10 gumdrops in the inner area and outer area
    for (let i = 0; i < 10; i++) {
      const pathIdx = Math.floor((i / 10) * n) % n;
      const p = path[pathIdx];
      // Place some inside the track loop, some outside
      const side = i < 5 ? -1 : 1;
      const dist = halfWidth + 15 + Math.random() * 20;

      const radius = 2 + Math.random() * 2;
      const geo = new THREE.SphereGeometry(radius, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2);
      const mat = new THREE.MeshStandardMaterial({
        color: pickColor(),
        roughness: 0.5,
        metalness: 0.0,
      });
      const gumdrop = new THREE.Mesh(geo, mat);
      gumdrop.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );
      gumdrop.castShadow = false;
      gumdrop.receiveShadow = true;

      this.scene.add(gumdrop);
      this.decorations.push(gumdrop);
    }

    // ---- 3. Candy Cane Poles ----
    // Place every ~30 path points along the road edges
    for (let i = 0; i < n; i += 30) {
      for (const side of [-1, 1]) {
        const p = path[i];
        const dist = halfWidth + 1;

        const group = new THREE.Group();

        // Build a candy cane: vertical cylinder with red-white stripes
        const poleHeight = 3;
        const segmentCount = 6;
        const segHeight = poleHeight / segmentCount;

        for (let s = 0; s < segmentCount; s++) {
          const segGeo = new THREE.CylinderGeometry(0.2, 0.2, segHeight, 8);
          const segMat = new THREE.MeshStandardMaterial({
            color: s % 2 === 0 ? 0xff0000 : 0xffffff,
            roughness: 0.5,
            metalness: 0.0,
          });
          const seg = new THREE.Mesh(segGeo, segMat);
          seg.position.y = segHeight * s + segHeight / 2;
          group.add(seg);
        }

        // Curved top (a small torus arc)
        const hookGeo = new THREE.TorusGeometry(0.5, 0.2, 8, 12, Math.PI);
        const hookMat = new THREE.MeshStandardMaterial({
          color: 0xff0000,
          roughness: 0.5,
          metalness: 0.0,
        });
        const hook = new THREE.Mesh(hookGeo, hookMat);
        hook.position.y = poleHeight + 0.3;
        hook.rotation.z = Math.PI;
        group.add(hook);

        group.position.set(
          p.x + p.nx * side * dist,
          0,
          p.z + p.nz * side * dist
        );
        group.castShadow = false;

        this.scene.add(group);
        this.decorations.push(group);
      }
    }

    // ---- 4. Cotton Candy Trees ----
    // Clusters of spheres in pink/blue/white
    for (let i = 0; i < 8; i++) {
      const pathIdx = Math.floor(((i + 0.5) / 8) * n) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 20 + Math.random() * 15;

      const group = new THREE.Group();

      // Trunk
      const trunkGeo = new THREE.CylinderGeometry(0.3, 0.4, 3, 8);
      const trunkMat = new THREE.MeshStandardMaterial({
        color: 0x8B5A2B,
        roughness: 0.9,
        metalness: 0.0,
      });
      const trunk = new THREE.Mesh(trunkGeo, trunkMat);
      trunk.position.y = 1.5;
      trunk.castShadow = false;
      group.add(trunk);

      // Cotton candy puffs
      const puffColors = [0xff99cc, 0x99ccff, 0xffffff, 0xffccee, 0xccddff];
      const puffCount = 4 + Math.floor(Math.random() * 3);
      for (let j = 0; j < puffCount; j++) {
        const puffRadius = 1.2 + Math.random() * 0.8;
        const puffGeo = new THREE.SphereGeometry(puffRadius, 12, 8);
        const puffMat = new THREE.MeshStandardMaterial({
          color: puffColors[Math.floor(Math.random() * puffColors.length)],
          roughness: 0.6,
          metalness: 0.0,
        });
        const puff = new THREE.Mesh(puffGeo, puffMat);
        puff.position.set(
          (Math.random() - 0.5) * 2,
          3.5 + (Math.random() - 0.5) * 1.5,
          (Math.random() - 0.5) * 2
        );
        puff.castShadow = false;
        group.add(puff);
      }

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 5. Sprinkle Patches on the Ground ----
    // Small colorful cylinders scattered in the grass
    for (let i = 0; i < 40; i++) {
      const pathIdx = Math.floor(Math.random() * n);
      const p = path[pathIdx];
      const side = Math.random() > 0.5 ? 1 : -1;
      const dist = halfWidth + 3 + Math.random() * 30;

      const geo = new THREE.CylinderGeometry(0.15, 0.15, 0.8, 6);
      const mat = new THREE.MeshStandardMaterial({
        color: pickColor(),
        roughness: 0.5,
        metalness: 0.0,
      });
      const sprinkle = new THREE.Mesh(geo, mat);
      sprinkle.position.set(
        p.x + p.nx * side * dist,
        0.05,
        p.z + p.nz * side * dist
      );
      // Tilt the sprinkle to lie on the ground
      sprinkle.rotation.z = Math.PI / 2 + (Math.random() - 0.5) * 0.5;
      sprinkle.rotation.y = Math.random() * Math.PI * 2;

      this.scene.add(sprinkle);
      this.decorations.push(sprinkle);
    }

    // ---- 6. On-Track Candy Wrappers (flat colored planes on the road) ----
    for (let i = 0; i < 5; i++) {
      const pathIdx = Math.floor(Math.random() * n);
      const p = path[pathIdx];
      // Place on the road, randomly across the width
      const lateralOffset = (Math.random() - 0.5) * halfWidth * 1.4;

      const wrapperGeo = new THREE.PlaneGeometry(1.2, 0.7);
      wrapperGeo.rotateX(-Math.PI / 2);
      const wrapperColors = [0xff4488, 0x44ccff, 0xffdd00, 0x66ff66, 0xcc44ff];
      const wrapperMat = new THREE.MeshStandardMaterial({
        color: wrapperColors[i % wrapperColors.length],
        roughness: 0.6,
        metalness: 0.1,
        side: THREE.DoubleSide,
      });
      const wrapper = new THREE.Mesh(wrapperGeo, wrapperMat);
      wrapper.position.set(
        p.x + p.nx * lateralOffset,
        0.025,
        p.z + p.nz * lateralOffset
      );
      wrapper.rotation.y = Math.random() * Math.PI * 2;

      this.scene.add(wrapper);
      this.decorations.push(wrapper);
    }

    // ---- 7. Small Gummy Bears (at road edges) ----
    for (let i = 0; i < 4; i++) {
      const pathIdx = Math.floor((i / 4) * n + n * 0.12) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? 1 : -1;
      const dist = halfWidth * 0.85; // near the road edge

      const group = new THREE.Group();
      const gummyColors = [0xff3333, 0x33ff33, 0xffff33, 0xff9933];
      const gummyColor = gummyColors[i % gummyColors.length];

      // Body: hemisphere
      const bodyGeo = new THREE.SphereGeometry(0.35, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2);
      const bodyMat = new THREE.MeshStandardMaterial({
        color: gummyColor,
        roughness: 0.4,
        metalness: 0.1,
      });
      const body = new THREE.Mesh(bodyGeo, bodyMat);
      body.position.y = 0;
      group.add(body);

      // Head: small sphere on top
      const headGeo = new THREE.SphereGeometry(0.2, 8, 6);
      const headMat = new THREE.MeshStandardMaterial({
        color: gummyColor,
        roughness: 0.4,
        metalness: 0.1,
      });
      const head = new THREE.Mesh(headGeo, headMat);
      head.position.y = 0.45;
      group.add(head);

      group.position.set(
        p.x + p.nx * side * dist,
        0.02,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 8. Chocolate Chip Bumps (small dark brown hemispheres on the road) ----
    for (let i = 0; i < 6; i++) {
      const pathIdx = Math.floor(Math.random() * n);
      const p = path[pathIdx];
      const lateralOffset = (Math.random() - 0.5) * halfWidth * 1.2;

      const chipGeo = new THREE.SphereGeometry(
        0.2 + Math.random() * 0.15,
        8,
        6,
        0,
        Math.PI * 2,
        0,
        Math.PI / 2
      );
      const chipMat = new THREE.MeshStandardMaterial({
        color: 0x3d1c02,
        roughness: 0.7,
        metalness: 0.0,
      });
      const chip = new THREE.Mesh(chipGeo, chipMat);
      chip.position.set(
        p.x + p.nx * lateralOffset,
        0.02,
        p.z + p.nz * lateralOffset
      );

      this.scene.add(chip);
      this.decorations.push(chip);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Giant Bathroom Decorations
  // ────────────────────────────────────────────────────────────

  private buildBathroomDecorations(path: TrackPoint[], mapDef: MapDef): void {
    const halfWidth = mapDef.roadWidth / 2;
    const n = path.length;

    // ---- 1. Giant Tile Floor ----
    this.buildTileFloor();

    // ---- 2. Giant Soap Bars ----
    const soapColors = [0xaaddff, 0xbbffbb, 0xffbbcc, 0xffddaa, 0xddbbff];
    for (let i = 0; i < 6; i++) {
      const pathIdx = Math.floor((i / 6) * n) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 15 + Math.random() * 20;

      const group = new THREE.Group();

      // Main soap body
      const bodyGeo = new THREE.BoxGeometry(15, 6, 8);
      const bodyMat = new THREE.MeshStandardMaterial({
        color: soapColors[i % soapColors.length],
        roughness: 0.3,
        metalness: 0.05,
      });
      const body = new THREE.Mesh(bodyGeo, bodyMat);
      body.position.y = 3;
      body.castShadow = false;
      body.receiveShadow = true;
      group.add(body);

      // Rounded edges: 4 cylinders along the long edges
      const edgeGeo = new THREE.CylinderGeometry(0.8, 0.8, 15, 8);
      const edgeMat = new THREE.MeshStandardMaterial({
        color: soapColors[i % soapColors.length],
        roughness: 0.3,
        metalness: 0.05,
      });
      const edgePositions = [
        { x: 0, y: 6, z: 4, rx: 0, rz: Math.PI / 2 },
        { x: 0, y: 6, z: -4, rx: 0, rz: Math.PI / 2 },
        { x: 0, y: 0, z: 4, rx: 0, rz: Math.PI / 2 },
        { x: 0, y: 0, z: -4, rx: 0, rz: Math.PI / 2 },
      ];
      for (const ep of edgePositions) {
        const edge = new THREE.Mesh(edgeGeo, edgeMat);
        edge.position.set(ep.x, ep.y, ep.z);
        edge.rotation.x = ep.rx;
        edge.rotation.z = ep.rz;
        group.add(edge);
      }

      // "SOAP" label: flat box on top face as a lighter rectangle
      const labelGeo = new THREE.BoxGeometry(8, 0.15, 3);
      const labelMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.4,
        metalness: 0.0,
      });
      const label = new THREE.Mesh(labelGeo, labelMat);
      label.position.y = 6.08;
      group.add(label);

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );
      group.rotation.y = Math.random() * Math.PI * 2;

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 3. Rubber Ducks ----
    for (let i = 0; i < 3; i++) {
      // Place near curves (use specific path indices that tend to be in bends)
      const curveIndices = [
        Math.floor(n * 0.15),
        Math.floor(n * 0.45),
        Math.floor(n * 0.75),
      ];
      const pathIdx = curveIndices[i % curveIndices.length];
      const p = path[pathIdx];
      const side = i % 2 === 0 ? 1 : -1;
      const dist = halfWidth + 18 + Math.random() * 10;

      const group = new THREE.Group();

      // Body: large yellow sphere
      const bodyGeo = new THREE.SphereGeometry(8, 20, 16);
      const bodyMat = new THREE.MeshStandardMaterial({
        color: 0xffdd00,
        roughness: 0.4,
        metalness: 0.05,
      });
      const body = new THREE.Mesh(bodyGeo, bodyMat);
      body.position.y = 6; // partially embedded in ground
      body.scale.set(1, 0.8, 1.1); // slightly squished, elongated
      body.castShadow = false;
      group.add(body);

      // Head: smaller yellow sphere on top front
      const headGeo = new THREE.SphereGeometry(5, 16, 12);
      const headMat = new THREE.MeshStandardMaterial({
        color: 0xffdd00,
        roughness: 0.4,
        metalness: 0.05,
      });
      const head = new THREE.Mesh(headGeo, headMat);
      head.position.set(0, 12, 4);
      head.castShadow = false;
      group.add(head);

      // Beak: orange cone
      const beakGeo = new THREE.ConeGeometry(1.5, 4, 8);
      const beakMat = new THREE.MeshStandardMaterial({
        color: 0xff8800,
        roughness: 0.5,
        metalness: 0.0,
      });
      const beak = new THREE.Mesh(beakGeo, beakMat);
      beak.position.set(0, 12, 9);
      beak.rotation.x = Math.PI / 2;
      group.add(beak);

      // Eyes: two small black spheres
      for (const ex of [-1.8, 1.8]) {
        const eyeGeo = new THREE.SphereGeometry(0.8, 8, 8);
        const eyeMat = new THREE.MeshStandardMaterial({
          color: 0x111111,
          roughness: 0.2,
          metalness: 0.0,
        });
        const eye = new THREE.Mesh(eyeGeo, eyeMat);
        eye.position.set(ex, 14, 7);
        group.add(eye);

        // Eye highlight
        const highlightGeo = new THREE.SphereGeometry(0.3, 6, 6);
        const highlightMat = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          roughness: 0.1,
          metalness: 0.0,
        });
        const highlight = new THREE.Mesh(highlightGeo, highlightMat);
        highlight.position.set(ex + 0.3, 14.3, 7.5);
        group.add(highlight);
      }

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );
      // Face toward the track
      group.rotation.y = Math.atan2(p.nx * side, p.nz * side) + Math.PI;

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 4. Giant Toothbrushes ----
    for (let i = 0; i < 2; i++) {
      const pathIdx = Math.floor(n * (i === 0 ? 0.3 : 0.7));
      const p = path[pathIdx];
      const side = i === 0 ? 1 : -1;
      const dist = halfWidth + 25;

      const group = new THREE.Group();

      // Handle: long cylinder
      const handleGeo = new THREE.CylinderGeometry(1.2, 1.0, 40, 12);
      const handleMat = new THREE.MeshStandardMaterial({
        color: i === 0 ? 0x4488ff : 0xff4488,
        roughness: 0.3,
        metalness: 0.2,
      });
      const handle = new THREE.Mesh(handleGeo, handleMat);
      handle.position.y = 0;
      handle.rotation.z = Math.PI / 2; // lying on the ground
      handle.castShadow = false;
      group.add(handle);

      // Bristle head: box
      const bristleBaseGeo = new THREE.BoxGeometry(8, 2, 4);
      const bristleBaseMat = new THREE.MeshStandardMaterial({
        color: i === 0 ? 0x4488ff : 0xff4488,
        roughness: 0.3,
        metalness: 0.2,
      });
      const bristleBase = new THREE.Mesh(bristleBaseGeo, bristleBaseMat);
      bristleBase.position.set(24, 0, 0);
      group.add(bristleBase);

      // Bristles: many small cylinders
      for (let bx = -3; bx <= 3; bx += 1.5) {
        for (let bz = -1.5; bz <= 1.5; bz += 1) {
          const bristleGeo = new THREE.CylinderGeometry(0.2, 0.2, 2, 4);
          const bristleMat = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            roughness: 0.8,
            metalness: 0.0,
          });
          const bristle = new THREE.Mesh(bristleGeo, bristleMat);
          bristle.position.set(24 + bx, 2, bz);
          group.add(bristle);
        }
      }

      group.position.set(
        p.x + p.nx * side * dist,
        1.2, // slight elevation so handle sits on ground
        p.z + p.nz * side * dist
      );
      group.rotation.y = Math.atan2(p.dz, p.dx) + (Math.random() - 0.5) * 0.5;

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 5. Toilet Paper Rolls ----
    for (let i = 0; i < 2; i++) {
      const pathIdx = Math.floor(n * (i === 0 ? 0.2 : 0.6));
      const p = path[pathIdx];
      const side = i === 0 ? -1 : 1;
      const dist = halfWidth + 12;

      const group = new THREE.Group();

      // Outer roll: white cylinder on its side
      const outerGeo = new THREE.CylinderGeometry(5, 5, 12, 24);
      const outerMat = new THREE.MeshStandardMaterial({
        color: 0xfaf8f0,
        roughness: 0.7,
        metalness: 0.0,
      });
      const outer = new THREE.Mesh(outerGeo, outerMat);
      outer.rotation.z = Math.PI / 2; // on its side
      outer.castShadow = false;
      group.add(outer);

      // Inner cardboard tube
      const innerGeo = new THREE.CylinderGeometry(2, 2, 12.5, 16);
      const innerMat = new THREE.MeshStandardMaterial({
        color: 0xcc9966,
        roughness: 0.9,
        metalness: 0.0,
      });
      const inner = new THREE.Mesh(innerGeo, innerMat);
      inner.rotation.z = Math.PI / 2;
      group.add(inner);

      // Ring at each end
      for (const xOff of [-6.1, 6.1]) {
        const ringGeo = new THREE.RingGeometry(2, 5, 24);
        const ringMat = new THREE.MeshStandardMaterial({
          color: 0xfaf8f0,
          roughness: 0.7,
          metalness: 0.0,
          side: THREE.DoubleSide,
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.position.x = xOff;
        ring.rotation.y = Math.PI / 2;
        group.add(ring);
      }

      // Unrolled paper trail: a flat ribbon coming off the roll
      const trailGeo = new THREE.PlaneGeometry(3.5, 15);
      const trailMat = new THREE.MeshStandardMaterial({
        color: 0xfaf8f0,
        roughness: 0.8,
        metalness: 0.0,
        side: THREE.DoubleSide,
      });
      const trail = new THREE.Mesh(trailGeo, trailMat);
      trail.position.set(0, -4.5, 8);
      trail.rotation.x = -0.3;
      group.add(trail);

      group.position.set(
        p.x + p.nx * side * dist,
        5,
        p.z + p.nz * side * dist
      );
      group.rotation.y = Math.atan2(p.dz, p.dx);

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 6. Giant Bubbles ----
    for (let i = 0; i < 12; i++) {
      const pathIdx = Math.floor(Math.random() * n);
      const p = path[pathIdx];
      const side = Math.random() > 0.5 ? 1 : -1;
      const dist = Math.random() * 60;

      const radius = 3 + Math.random() * 4;
      const geo = new THREE.SphereGeometry(radius, 20, 16);
      const mat = new THREE.MeshStandardMaterial({
        color: 0xaaddff,
        transparent: true,
        opacity: 0.25,
        roughness: 0.0,
        metalness: 0.6,
        side: THREE.DoubleSide,
      });
      const bubble = new THREE.Mesh(geo, mat);
      bubble.position.set(
        p.x + p.nx * side * dist,
        10 + Math.random() * 25,
        p.z + p.nz * side * dist
      );

      this.scene.add(bubble);
      this.decorations.push(bubble);
    }

    // ---- 7. Giant Shampoo Bottles ----
    for (let i = 0; i < 2; i++) {
      const pathIdx = Math.floor(n * (i === 0 ? 0.1 : 0.55));
      const p = path[pathIdx];
      const side = i === 0 ? 1 : -1;
      const dist = halfWidth + 30 + Math.random() * 10;

      const group = new THREE.Group();

      // Body: tall cylinder
      const bottleGeo = new THREE.CylinderGeometry(4, 4.5, 25, 16);
      const bottleMat = new THREE.MeshStandardMaterial({
        color: i === 0 ? 0x44bb44 : 0xbb44bb,
        roughness: 0.2,
        metalness: 0.3,
      });
      const bottle = new THREE.Mesh(bottleGeo, bottleMat);
      bottle.position.y = 12.5;
      bottle.castShadow = false;
      group.add(bottle);

      // Cap: smaller cylinder on top
      const capGeo = new THREE.CylinderGeometry(2.5, 3, 4, 12);
      const capMat = new THREE.MeshStandardMaterial({
        color: 0xdddddd,
        roughness: 0.2,
        metalness: 0.4,
      });
      const cap = new THREE.Mesh(capGeo, capMat);
      cap.position.y = 27;
      group.add(cap);

      // Label: lighter rectangle wrapped around the middle
      const labelGeo = new THREE.CylinderGeometry(4.6, 4.6, 10, 16, 1, true);
      const labelMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.5,
        metalness: 0.0,
        side: THREE.DoubleSide,
      });
      const label = new THREE.Mesh(labelGeo, labelMat);
      label.position.y = 12;
      group.add(label);

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 8. Water Puddles ----
    for (let i = 0; i < 5; i++) {
      const pathIdx = Math.floor(Math.random() * n);
      const p = path[pathIdx];
      const side = Math.random() > 0.5 ? 1 : -1;
      const dist = halfWidth + 5 + Math.random() * 25;

      const radius = 4 + Math.random() * 6;
      const geo = new THREE.CircleGeometry(radius, 24);
      geo.rotateX(-Math.PI / 2);
      const mat = new THREE.MeshStandardMaterial({
        color: 0x88bbee,
        transparent: true,
        opacity: 0.5,
        roughness: 0.0,
        metalness: 0.8,
      });
      const puddle = new THREE.Mesh(geo, mat);
      puddle.position.set(
        p.x + p.nx * side * dist,
        0.02,
        p.z + p.nz * side * dist
      );

      this.scene.add(puddle);
      this.decorations.push(puddle);
    }

    // ---- 9. Small Soap Bubbles at Road Edges ----
    for (let i = 0; i < 5; i++) {
      const pathIdx = Math.floor((i / 5) * n + n * 0.08) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? 1 : -1;
      const dist = halfWidth * 0.8 + Math.random() * halfWidth * 0.2;

      const bubbleRadius = 0.3 + Math.random() * 0.25;
      const bubbleGeo = new THREE.SphereGeometry(bubbleRadius, 12, 8);
      const bubbleMat = new THREE.MeshStandardMaterial({
        color: 0xccddff,
        transparent: true,
        opacity: 0.35,
        roughness: 0.0,
        metalness: 0.6,
        side: THREE.DoubleSide,
      });
      const bubble = new THREE.Mesh(bubbleGeo, bubbleMat);
      bubble.position.set(
        p.x + p.nx * side * dist,
        bubbleRadius + 0.02,
        p.z + p.nz * side * dist
      );

      this.scene.add(bubble);
      this.decorations.push(bubble);
    }

    // ---- 10. Scattered Cotton Balls (white fluffy sphere clusters) ----
    for (let i = 0; i < 4; i++) {
      const pathIdx = Math.floor((i / 4) * n + n * 0.15) % n;
      const p = path[pathIdx];
      const lateralOffset = (Math.random() - 0.5) * halfWidth * 1.0;

      const group = new THREE.Group();

      // 3-4 overlapping small white spheres make a cotton ball
      const puffCount = 3 + Math.floor(Math.random() * 2);
      for (let j = 0; j < puffCount; j++) {
        const puffRadius = 0.2 + Math.random() * 0.15;
        const puffGeo = new THREE.SphereGeometry(puffRadius, 8, 6);
        const puffMat = new THREE.MeshStandardMaterial({
          color: 0xfafafa,
          roughness: 0.9,
          metalness: 0.0,
        });
        const puff = new THREE.Mesh(puffGeo, puffMat);
        puff.position.set(
          (Math.random() - 0.5) * 0.3,
          puffRadius * 0.5,
          (Math.random() - 0.5) * 0.3
        );
        group.add(puff);
      }

      group.position.set(
        p.x + p.nx * lateralOffset,
        0.02,
        p.z + p.nz * lateralOffset
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 11. Tiny Rubber Duck Toys (near road edges) ----
    for (let i = 0; i < 4; i++) {
      const pathIdx = Math.floor((i / 4) * n + n * 0.3) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? 1 : -1;
      const dist = halfWidth * 0.7 + Math.random() * halfWidth * 0.2;

      const group = new THREE.Group();

      // Tiny body: small yellow sphere
      const bodyGeo = new THREE.SphereGeometry(0.25, 8, 6);
      const bodyMat = new THREE.MeshStandardMaterial({
        color: 0xffdd00,
        roughness: 0.4,
        metalness: 0.05,
      });
      const body = new THREE.Mesh(bodyGeo, bodyMat);
      body.position.y = 0.2;
      body.scale.set(1, 0.8, 1.1);
      group.add(body);

      // Tiny head
      const headGeo = new THREE.SphereGeometry(0.15, 8, 6);
      const headMat = new THREE.MeshStandardMaterial({
        color: 0xffdd00,
        roughness: 0.4,
        metalness: 0.05,
      });
      const head = new THREE.Mesh(headGeo, headMat);
      head.position.set(0, 0.42, 0.12);
      group.add(head);

      // Tiny orange beak
      const beakGeo = new THREE.ConeGeometry(0.06, 0.15, 6);
      const beakMat = new THREE.MeshStandardMaterial({
        color: 0xff8800,
        roughness: 0.5,
        metalness: 0.0,
      });
      const beak = new THREE.Mesh(beakGeo, beakMat);
      beak.position.set(0, 0.42, 0.28);
      beak.rotation.x = Math.PI / 2;
      group.add(beak);

      group.position.set(
        p.x + p.nx * side * dist,
        0.02,
        p.z + p.nz * side * dist
      );
      // Random facing direction
      group.rotation.y = Math.random() * Math.PI * 2;

      this.scene.add(group);
      this.decorations.push(group);
    }
  }

  private buildTileFloor(): void {
    const tileGroup = new THREE.Group();
    const tileSize = 30;
    const gap = 0.5;
    const gridCount = 10; // 10x10 tiles covers 300x300

    const tileGeo = new THREE.BoxGeometry(tileSize - gap, 0.15, tileSize - gap);

    const whiteMat = new THREE.MeshStandardMaterial({
      color: 0xeeeeff,
      roughness: 0.3,
      metalness: 0.1,
    });
    const blueMat = new THREE.MeshStandardMaterial({
      color: 0xbbccdd,
      roughness: 0.3,
      metalness: 0.1,
    });

    const halfGrid = (gridCount * tileSize) / 2;
    for (let row = 0; row < gridCount; row++) {
      for (let col = 0; col < gridCount; col++) {
        const isWhite = (row + col) % 2 === 0;
        const tile = new THREE.Mesh(tileGeo, isWhite ? whiteMat : blueMat);
        tile.position.set(
          col * tileSize - halfGrid + tileSize / 2,
          -0.03,
          row * tileSize - halfGrid + tileSize / 2
        );
        tile.receiveShadow = true;
        tileGroup.add(tile);
      }
    }

    // Grout lines (darker narrow strips between tiles)
    const groutMat = new THREE.MeshStandardMaterial({
      color: 0x888899,
      roughness: 0.9,
      metalness: 0.0,
    });

    // Horizontal grout lines
    for (let row = 0; row <= gridCount; row++) {
      const groutGeo = new THREE.BoxGeometry(gridCount * tileSize, 0.1, gap);
      const grout = new THREE.Mesh(groutGeo, groutMat);
      grout.position.set(0, -0.02, row * tileSize - halfGrid);
      grout.receiveShadow = true;
      tileGroup.add(grout);
    }

    // Vertical grout lines
    for (let col = 0; col <= gridCount; col++) {
      const groutGeo = new THREE.BoxGeometry(gap, 0.1, gridCount * tileSize);
      const grout = new THREE.Mesh(groutGeo, groutMat);
      grout.position.set(col * tileSize - halfGrid, -0.02, 0);
      grout.receiveShadow = true;
      tileGroup.add(grout);
    }

    this.tileGroup = tileGroup;
    this.scene.add(tileGroup);
    this.decorations.push(tileGroup);
  }

  // ────────────────────────────────────────────────────────────
  // Desert Canyon Decorations
  // ────────────────────────────────────────────────────────────

  private buildDesertCanyonDecorations(path: TrackPoint[], mapDef: MapDef): void {
    const halfWidth = mapDef.roadWidth / 2;
    const n = path.length;

    // ---- 1. Cacti ----
    for (let i = 0; i < 14; i++) {
      const pathIdx = Math.floor((i / 14) * n) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 4 + Math.random() * 10;

      const group = new THREE.Group();

      // Main trunk
      const trunkGeo = new THREE.CylinderGeometry(0.5, 0.6, 5, 8);
      const cactusMat = new THREE.MeshStandardMaterial({
        color: 0x2d8a3e,
        roughness: 0.8,
        metalness: 0.0,
      });
      const trunk = new THREE.Mesh(trunkGeo, cactusMat);
      trunk.position.y = 2.5;
      trunk.castShadow = false;
      group.add(trunk);

      // Top sphere
      const topGeo = new THREE.SphereGeometry(0.55, 8, 6);
      const top = new THREE.Mesh(topGeo, cactusMat);
      top.position.y = 5.2;
      group.add(top);

      // Arms (on every other cactus)
      if (i % 2 === 0) {
        const armGeo = new THREE.CylinderGeometry(0.3, 0.35, 2.5, 6);
        const arm1 = new THREE.Mesh(armGeo, cactusMat);
        arm1.position.set(0.8, 3.5, 0);
        arm1.rotation.z = -Math.PI / 4;
        group.add(arm1);

        const armTop = new THREE.SphereGeometry(0.35, 6, 4);
        const armBall = new THREE.Mesh(armTop, cactusMat);
        armBall.position.set(1.6, 4.4, 0);
        group.add(armBall);
      }

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 2. Rock Arches ----
    for (let i = 0; i < 3; i++) {
      const pathIdx = Math.floor((i / 3) * n + n * 0.1) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? 1 : -1;
      const dist = halfWidth + 20 + Math.random() * 10;

      const group = new THREE.Group();
      const rockMat = new THREE.MeshStandardMaterial({
        color: 0xb85c38,
        roughness: 0.9,
        metalness: 0.0,
      });

      // Left pillar
      const pillarGeo = new THREE.CylinderGeometry(1.5, 2, 12, 8);
      const leftPillar = new THREE.Mesh(pillarGeo, rockMat);
      leftPillar.position.set(-4, 6, 0);
      leftPillar.castShadow = false;
      group.add(leftPillar);

      // Right pillar
      const rightPillar = new THREE.Mesh(pillarGeo, rockMat);
      rightPillar.position.set(4, 6, 0);
      rightPillar.castShadow = false;
      group.add(rightPillar);

      // Arch span (box stretched between pillars)
      const archGeo = new THREE.BoxGeometry(10, 2.5, 3);
      const arch = new THREE.Mesh(archGeo, rockMat);
      arch.position.set(0, 12.5, 0);
      arch.castShadow = false;
      group.add(arch);

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );
      group.rotation.y = Math.atan2(p.dz, p.dx);

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 3. Sand Dune Mounds ----
    for (let i = 0; i < 8; i++) {
      const pathIdx = Math.floor((i / 8) * n) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 15 + Math.random() * 25;

      const radius = 3 + Math.random() * 4;
      const duneGeo = new THREE.SphereGeometry(radius, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2);
      const duneMat = new THREE.MeshStandardMaterial({
        color: 0xe8c878,
        roughness: 0.95,
        metalness: 0.0,
      });
      const dune = new THREE.Mesh(duneGeo, duneMat);
      dune.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );
      dune.castShadow = false;
      dune.receiveShadow = true;

      this.scene.add(dune);
      this.decorations.push(dune);
    }

    // ---- 4. Tumbleweeds ----
    for (let i = 0; i < 10; i++) {
      const pathIdx = Math.floor(Math.random() * n);
      const p = path[pathIdx];
      const side = Math.random() > 0.5 ? 1 : -1;
      const dist = halfWidth + 2 + Math.random() * 20;

      const radius = 0.5 + Math.random() * 0.5;
      const twGeo = new THREE.SphereGeometry(radius, 8, 6);
      const twMat = new THREE.MeshStandardMaterial({
        color: 0x8b6914,
        roughness: 0.9,
        metalness: 0.0,
      });
      const tw = new THREE.Mesh(twGeo, twMat);
      tw.position.set(
        p.x + p.nx * side * dist,
        radius,
        p.z + p.nz * side * dist
      );

      this.scene.add(tw);
      this.decorations.push(tw);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Space Station Decorations
  // ────────────────────────────────────────────────────────────

  private buildSpaceStationDecorations(path: TrackPoint[], mapDef: MapDef): void {
    const halfWidth = mapDef.roadWidth / 2;
    const n = path.length;

    const neonColors = [0x00aaff, 0x8800ff, 0x00ffcc, 0x4466ff, 0xaa00ff];
    const pickNeon = () => neonColors[Math.floor(Math.random() * neonColors.length)];

    // ---- 1. Glowing Pillars ----
    for (let i = 0; i < 14; i++) {
      const pathIdx = Math.floor((i / 14) * n) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 3 + Math.random() * 5;

      const group = new THREE.Group();

      // Dark pillar body
      const pillarGeo = new THREE.CylinderGeometry(0.5, 0.5, 6, 8);
      const pillarMat = new THREE.MeshStandardMaterial({
        color: 0x222244,
        roughness: 0.4,
        metalness: 0.6,
      });
      const pillar = new THREE.Mesh(pillarGeo, pillarMat);
      pillar.position.y = 3;
      pillar.castShadow = false;
      group.add(pillar);

      // Emissive neon strip (thin cylinder wrapped around)
      const neonColor = pickNeon();
      const stripGeo = new THREE.CylinderGeometry(0.55, 0.55, 0.3, 8);
      const stripMat = new THREE.MeshStandardMaterial({
        color: neonColor,
        emissive: neonColor,
        emissiveIntensity: 2.0,
        roughness: 0.2,
        metalness: 0.0,
      });
      // Multiple strips along the pillar
      for (const yOff of [1.5, 3.0, 4.5]) {
        const strip = new THREE.Mesh(stripGeo, stripMat);
        strip.position.y = yOff;
        group.add(strip);
      }

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 2. Floating Platform Blocks ----
    for (let i = 0; i < 8; i++) {
      const pathIdx = Math.floor((i / 8) * n) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 10 + Math.random() * 20;

      const size = 2 + Math.random() * 3;
      const blockGeo = new THREE.BoxGeometry(size, size * 0.5, size);
      const blockMat = new THREE.MeshStandardMaterial({
        color: 0x1a1a3e,
        roughness: 0.3,
        metalness: 0.7,
      });
      const block = new THREE.Mesh(blockGeo, blockMat);
      block.position.set(
        p.x + p.nx * side * dist,
        4 + Math.random() * 8,
        p.z + p.nz * side * dist
      );
      block.rotation.y = Math.random() * Math.PI;
      block.castShadow = false;

      this.scene.add(block);
      this.decorations.push(block);
    }

    // ---- 3. Antenna Towers ----
    for (let i = 0; i < 6; i++) {
      const pathIdx = Math.floor((i / 6) * n + n * 0.05) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? 1 : -1;
      const dist = halfWidth + 8 + Math.random() * 12;

      const group = new THREE.Group();

      // Thin tower
      const towerGeo = new THREE.CylinderGeometry(0.15, 0.2, 10, 6);
      const towerMat = new THREE.MeshStandardMaterial({
        color: 0x333366,
        roughness: 0.5,
        metalness: 0.5,
      });
      const tower = new THREE.Mesh(towerGeo, towerMat);
      tower.position.y = 5;
      tower.castShadow = false;
      group.add(tower);

      // Emissive tip
      const neonColor = pickNeon();
      const tipGeo = new THREE.SphereGeometry(0.4, 8, 6);
      const tipMat = new THREE.MeshStandardMaterial({
        color: neonColor,
        emissive: neonColor,
        emissiveIntensity: 3.0,
        roughness: 0.1,
        metalness: 0.0,
      });
      const tip = new THREE.Mesh(tipGeo, tipMat);
      tip.position.y = 10.4;
      group.add(tip);

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 4. Ring Structures ----
    for (let i = 0; i < 4; i++) {
      const pathIdx = Math.floor((i / 4) * n + n * 0.12) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 15 + Math.random() * 15;

      const neonColor = pickNeon();
      const ringGeo = new THREE.TorusGeometry(3, 0.3, 12, 24);
      const ringMat = new THREE.MeshStandardMaterial({
        color: neonColor,
        emissive: neonColor,
        emissiveIntensity: 1.5,
        roughness: 0.2,
        metalness: 0.3,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.set(
        p.x + p.nx * side * dist,
        5 + Math.random() * 5,
        p.z + p.nz * side * dist
      );
      ring.rotation.x = Math.PI / 2 + (Math.random() - 0.5) * 0.5;
      ring.rotation.y = Math.random() * Math.PI;

      this.scene.add(ring);
      this.decorations.push(ring);
    }

    // ---- 5. Glowing Orbs Along Track Edges ----
    for (let i = 0; i < 24; i++) {
      const pathIdx = Math.floor((i / 24) * n) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 2.5;

      const orbColor = pickNeon();
      const orbGeo = new THREE.SphereGeometry(0.2, 8, 6);
      const orbMat = new THREE.MeshStandardMaterial({
        color: orbColor,
        emissive: orbColor,
        emissiveIntensity: 2.5,
        roughness: 0.0,
        metalness: 0.0,
      });
      const orb = new THREE.Mesh(orbGeo, orbMat);
      orb.position.set(
        p.x + p.nx * side * dist,
        0.5,
        p.z + p.nz * side * dist
      );

      this.scene.add(orb);
      this.decorations.push(orb);
    }

    // ---- 6. Satellite Dishes ----
    for (let i = 0; i < 4; i++) {
      const pathIdx = Math.floor((i / 4) * n + n * 0.2) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 18 + Math.random() * 12;

      const group = new THREE.Group();

      const poleMat = new THREE.MeshStandardMaterial({
        color: 0x444466,
        roughness: 0.3,
        metalness: 0.7,
      });

      // Support pole
      const poleGeo = new THREE.CylinderGeometry(0.35, 0.5, 6, 8);
      const pole = new THREE.Mesh(poleGeo, poleMat);
      pole.position.y = 3;
      group.add(pole);

      // Dish: hemisphere bowl
      const dishGeo = new THREE.SphereGeometry(3, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2);
      const dishMat = new THREE.MeshStandardMaterial({
        color: 0x666688,
        roughness: 0.2,
        metalness: 0.8,
        side: THREE.DoubleSide,
      });
      const dish = new THREE.Mesh(dishGeo, dishMat);
      dish.position.y = 6.5;
      dish.rotation.x = Math.PI * 0.7;
      group.add(dish);

      // Receiver arm
      const armGeo = new THREE.CylinderGeometry(0.08, 0.08, 2.5, 4);
      const arm = new THREE.Mesh(armGeo, poleMat);
      arm.position.set(0, 7.5, -1);
      arm.rotation.x = -0.3;
      group.add(arm);

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Haunted Mansion Decorations
  // ────────────────────────────────────────────────────────────

  private buildHauntedMansionDecorations(path: TrackPoint[], mapDef: MapDef): void {
    const halfWidth = mapDef.roadWidth / 2;
    const n = path.length;

    // ---- 1. Dead Trees ----
    for (let i = 0; i < 10; i++) {
      const pathIdx = Math.floor((i / 10) * n) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 5 + Math.random() * 12;

      const group = new THREE.Group();
      const treeMat = new THREE.MeshStandardMaterial({
        color: 0x3d2b1f,
        roughness: 0.9,
        metalness: 0.0,
      });

      // Trunk
      const trunkGeo = new THREE.CylinderGeometry(0.3, 0.5, 6, 6);
      const trunk = new THREE.Mesh(trunkGeo, treeMat);
      trunk.position.y = 3;
      trunk.castShadow = false;
      group.add(trunk);

      // Branch 1
      const branch1Geo = new THREE.CylinderGeometry(0.15, 0.2, 3, 5);
      const branch1 = new THREE.Mesh(branch1Geo, treeMat);
      branch1.position.set(0.8, 5, 0.3);
      branch1.rotation.z = -Math.PI / 4;
      group.add(branch1);

      // Branch 2
      const branch2Geo = new THREE.CylinderGeometry(0.12, 0.18, 2.5, 5);
      const branch2 = new THREE.Mesh(branch2Geo, treeMat);
      branch2.position.set(-0.6, 4.5, -0.4);
      branch2.rotation.z = Math.PI / 5;
      group.add(branch2);

      // Branch 3 (smaller)
      if (i % 3 === 0) {
        const branch3Geo = new THREE.CylinderGeometry(0.1, 0.15, 2, 5);
        const branch3 = new THREE.Mesh(branch3Geo, treeMat);
        branch3.position.set(0.3, 5.5, 0.6);
        branch3.rotation.x = -Math.PI / 5;
        branch3.rotation.z = -Math.PI / 6;
        group.add(branch3);
      }

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 2. Gravestones ----
    for (let i = 0; i < 8; i++) {
      const pathIdx = Math.floor((i / 8) * n + n * 0.06) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? 1 : -1;
      const dist = halfWidth + 3 + Math.random() * 8;

      const stoneGeo = new THREE.BoxGeometry(1.2, 2, 0.4);
      const stoneMat = new THREE.MeshStandardMaterial({
        color: 0x777788,
        roughness: 0.85,
        metalness: 0.05,
      });
      const stone = new THREE.Mesh(stoneGeo, stoneMat);
      stone.position.set(
        p.x + p.nx * side * dist,
        1,
        p.z + p.nz * side * dist
      );
      // Slight tilt for eerie effect
      stone.rotation.z = (Math.random() - 0.5) * 0.2;
      stone.rotation.x = (Math.random() - 0.5) * 0.15;
      stone.castShadow = false;

      this.scene.add(stone);
      this.decorations.push(stone);
    }

    // ---- 3. Ghost Spheres ----
    for (let i = 0; i < 6; i++) {
      const pathIdx = Math.floor((i / 6) * n + n * 0.15) % n;
      const p = path[pathIdx];
      const side = Math.random() > 0.5 ? 1 : -1;
      const dist = halfWidth + 5 + Math.random() * 15;

      const ghostGeo = new THREE.SphereGeometry(1 + Math.random() * 0.8, 12, 8);
      const ghostMat = new THREE.MeshStandardMaterial({
        color: 0xddddee,
        emissive: 0x444466,
        emissiveIntensity: 0.5,
        transparent: true,
        opacity: 0.35,
        roughness: 0.3,
        metalness: 0.0,
      });
      const ghost = new THREE.Mesh(ghostGeo, ghostMat);
      ghost.position.set(
        p.x + p.nx * side * dist,
        3 + Math.random() * 5,
        p.z + p.nz * side * dist
      );

      this.scene.add(ghost);
      this.decorations.push(ghost);
    }

    // ---- 4. Cobweb Planes ----
    for (let i = 0; i < 5; i++) {
      const pathIdx = Math.floor((i / 5) * n + n * 0.2) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 4 + Math.random() * 6;

      const webGeo = new THREE.PlaneGeometry(3, 3);
      const webMat = new THREE.MeshStandardMaterial({
        color: 0xcccccc,
        transparent: true,
        opacity: 0.2,
        roughness: 0.8,
        metalness: 0.0,
        side: THREE.DoubleSide,
      });
      const web = new THREE.Mesh(webGeo, webMat);
      web.position.set(
        p.x + p.nx * side * dist,
        2 + Math.random() * 3,
        p.z + p.nz * side * dist
      );
      web.rotation.y = Math.random() * Math.PI;
      web.rotation.x = (Math.random() - 0.5) * 0.3;

      this.scene.add(web);
      this.decorations.push(web);
    }

    // ---- 5. Flickering Point Lights ----
    for (let i = 0; i < 4; i++) {
      const pathIdx = Math.floor((i / 4) * n + n * 0.1) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? 1 : -1;
      const dist = halfWidth + 2;

      const light = new THREE.PointLight(0x8844cc, 0.6, 20);
      light.position.set(
        p.x + p.nx * side * dist,
        3,
        p.z + p.nz * side * dist
      );

      this.scene.add(light);
      this.decorations.push(light);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Ice World Decorations
  // ────────────────────────────────────────────────────────────

  private buildIceWorldDecorations(path: TrackPoint[], mapDef: MapDef): void {
    const halfWidth = mapDef.roadWidth / 2;
    const n = path.length;

    // ---- 1. Ice Crystals (elongated mirrored cones) ----
    for (let i = 0; i < 10; i++) {
      const pathIdx = Math.floor((i / 10) * n) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 4 + Math.random() * 10;

      const group = new THREE.Group();
      const crystalMat = new THREE.MeshStandardMaterial({
        color: 0x88ccff,
        emissive: 0x224466,
        emissiveIntensity: 0.3,
        transparent: true,
        opacity: 0.7,
        roughness: 0.1,
        metalness: 0.4,
      });

      const height = 2 + Math.random() * 2;

      // Upper cone (pointing up)
      const upperGeo = new THREE.ConeGeometry(0.6, height, 6);
      const upper = new THREE.Mesh(upperGeo, crystalMat);
      upper.position.y = height * 0.8;
      group.add(upper);

      // Lower cone (pointing down, mirrored)
      const lowerGeo = new THREE.ConeGeometry(0.6, height * 0.6, 6);
      const lower = new THREE.Mesh(lowerGeo, crystalMat);
      lower.position.y = height * 0.2;
      lower.rotation.x = Math.PI;
      group.add(lower);

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );
      // Slight random tilt
      group.rotation.z = (Math.random() - 0.5) * 0.15;

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 2. Snow Mounds ----
    for (let i = 0; i < 8; i++) {
      const pathIdx = Math.floor((i / 8) * n + n * 0.05) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 12 + Math.random() * 20;

      const radius = 2 + Math.random() * 3;
      const moundGeo = new THREE.SphereGeometry(radius, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2);
      const moundMat = new THREE.MeshStandardMaterial({
        color: 0xf0f5ff,
        roughness: 0.9,
        metalness: 0.0,
      });
      const mound = new THREE.Mesh(moundGeo, moundMat);
      mound.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );
      mound.castShadow = false;
      mound.receiveShadow = true;

      this.scene.add(mound);
      this.decorations.push(mound);
    }

    // ---- 3. Frozen Pine Trees ----
    for (let i = 0; i < 10; i++) {
      const pathIdx = Math.floor((i / 10) * n + n * 0.08) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? 1 : -1;
      const dist = halfWidth + 6 + Math.random() * 12;

      const group = new THREE.Group();

      // Trunk
      const trunkGeo = new THREE.CylinderGeometry(0.25, 0.35, 2, 6);
      const trunkMat = new THREE.MeshStandardMaterial({
        color: 0x4a3728,
        roughness: 0.9,
        metalness: 0.0,
      });
      const trunk = new THREE.Mesh(trunkGeo, trunkMat);
      trunk.position.y = 1;
      trunk.castShadow = false;
      group.add(trunk);

      // Green cone (lower foliage)
      const foliage1Geo = new THREE.ConeGeometry(2, 3.5, 8);
      const foliageMat = new THREE.MeshStandardMaterial({
        color: 0x1a5c32,
        roughness: 0.7,
        metalness: 0.0,
      });
      const foliage1 = new THREE.Mesh(foliage1Geo, foliageMat);
      foliage1.position.y = 3.5;
      foliage1.castShadow = false;
      group.add(foliage1);

      // Green cone (upper foliage, smaller)
      const foliage2Geo = new THREE.ConeGeometry(1.4, 2.5, 8);
      const foliage2 = new THREE.Mesh(foliage2Geo, foliageMat);
      foliage2.position.y = 5.5;
      foliage2.castShadow = false;
      group.add(foliage2);

      // White snow cap on top
      const snowGeo = new THREE.ConeGeometry(0.9, 1.5, 8);
      const snowMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.6,
        metalness: 0.0,
      });
      const snow = new THREE.Mesh(snowGeo, snowMat);
      snow.position.y = 7;
      group.add(snow);

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 4. Icicle Formations ----
    for (let i = 0; i < 8; i++) {
      const pathIdx = Math.floor((i / 8) * n + n * 0.15) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? -1 : 1;
      const dist = halfWidth + 2 + Math.random() * 4;

      const group = new THREE.Group();
      const icicleMat = new THREE.MeshStandardMaterial({
        color: 0xddeeff,
        transparent: true,
        opacity: 0.75,
        roughness: 0.1,
        metalness: 0.3,
      });

      // Cluster of 3-4 icicles hanging from a horizontal bar
      const barGeo = new THREE.BoxGeometry(2.5, 0.3, 0.3);
      const barMat = new THREE.MeshStandardMaterial({
        color: 0x99bbdd,
        roughness: 0.5,
        metalness: 0.2,
      });
      const bar = new THREE.Mesh(barGeo, barMat);
      bar.position.y = 4;
      group.add(bar);

      const icicleCount = 3 + Math.floor(Math.random() * 2);
      for (let j = 0; j < icicleCount; j++) {
        const icicleLen = 1 + Math.random() * 2;
        const icicleGeo = new THREE.ConeGeometry(0.15, icicleLen, 5);
        const icicle = new THREE.Mesh(icicleGeo, icicleMat);
        icicle.position.set(
          -1 + j * (2 / (icicleCount - 1 || 1)),
          4 - icicleLen / 2 - 0.15,
          0
        );
        icicle.rotation.x = Math.PI; // pointing down
        group.add(icicle);
      }

      group.position.set(
        p.x + p.nx * side * dist,
        0,
        p.z + p.nz * side * dist
      );

      this.scene.add(group);
      this.decorations.push(group);
    }

    // ---- 5. Frozen Lake Surface Planes ----
    for (let i = 0; i < 3; i++) {
      const pathIdx = Math.floor((i / 3) * n + n * 0.15) % n;
      const p = path[pathIdx];
      const side = i % 2 === 0 ? 1 : -1;
      const dist = halfWidth + 25 + Math.random() * 15;

      const size = 15 + Math.random() * 10;
      const lakeGeo = new THREE.CircleGeometry(size, 24);
      lakeGeo.rotateX(-Math.PI / 2);
      const lakeMat = new THREE.MeshStandardMaterial({
        color: 0xbbddee,
        transparent: true,
        opacity: 0.7,
        roughness: 0.02,
        metalness: 0.9,
      });
      const lake = new THREE.Mesh(lakeGeo, lakeMat);
      lake.position.set(
        p.x + p.nx * side * dist,
        0.01,
        p.z + p.nz * side * dist
      );
      lake.receiveShadow = true;

      this.scene.add(lake);
      this.decorations.push(lake);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Map-Specific Ramp Styles
  // ────────────────────────────────────────────────────────────

  /** Desert Canyon ramp: sandy brown wedge with rocky texture */
  private buildDesertRamp(
    group: THREE.Group,
    width: number,
    length: number,
    height: number,
    index: number
  ): void {
    const sandColors = [0xc49a3c, 0xb8883a, 0xd4a84e];
    const mainColor = sandColors[index % sandColors.length];

    const wedgeGeo = this.buildRampWedge(width, length, height);
    const wedgeMat = new THREE.MeshStandardMaterial({
      color: mainColor,
      roughness: 0.9,
      metalness: 0.0,
    });
    const wedge = new THREE.Mesh(wedgeGeo, wedgeMat);
    wedge.position.y = 0.02;
    wedge.castShadow = false;
    wedge.receiveShadow = true;
    group.add(wedge);

    // Rocky stripe along the edges
    for (const side of [-1, 1]) {
      const edgeGeo = new THREE.BoxGeometry(0.4, height * 0.3, length);
      const edgeMat = new THREE.MeshStandardMaterial({
        color: 0x8b6331,
        roughness: 0.95,
        metalness: 0.0,
      });
      const edge = new THREE.Mesh(edgeGeo, edgeMat);
      edge.position.set(side * (width / 2 - 0.2), 0.15, 0);
      group.add(edge);
    }
  }

  /** Space Station ramp: dark metallic wedge with emissive edge */
  private buildSpaceRamp(
    group: THREE.Group,
    width: number,
    length: number,
    height: number,
    index: number
  ): void {
    const wedgeGeo = this.buildRampWedge(width, length, height);
    const wedgeMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a3e,
      roughness: 0.3,
      metalness: 0.7,
    });
    const wedge = new THREE.Mesh(wedgeGeo, wedgeMat);
    wedge.position.y = 0.02;
    wedge.castShadow = false;
    wedge.receiveShadow = true;
    group.add(wedge);

    // Emissive edge strips along sides
    const edgeColors = [0x00aaff, 0x8800ff, 0x00ffcc];
    const edgeColor = edgeColors[index % edgeColors.length];
    for (const side of [-1, 1]) {
      const edgeGeo = new THREE.BoxGeometry(0.2, 0.15, length + 0.2);
      const edgeMat = new THREE.MeshStandardMaterial({
        color: edgeColor,
        emissive: edgeColor,
        emissiveIntensity: 2.0,
        roughness: 0.1,
        metalness: 0.0,
      });
      const edge = new THREE.Mesh(edgeGeo, edgeMat);
      edge.position.set(side * (width / 2 + 0.1), 0.08, 0);
      group.add(edge);
    }
  }

  /** Haunted Mansion ramp: dark purple wedge */
  private buildHauntedRamp(
    group: THREE.Group,
    width: number,
    length: number,
    height: number,
    _index: number
  ): void {
    const wedgeGeo = this.buildRampWedge(width, length, height);
    const wedgeMat = new THREE.MeshStandardMaterial({
      color: 0x3d2255,
      roughness: 0.7,
      metalness: 0.1,
    });
    const wedge = new THREE.Mesh(wedgeGeo, wedgeMat);
    wedge.position.y = 0.02;
    wedge.castShadow = false;
    wedge.receiveShadow = true;
    group.add(wedge);

    // Faint ghostly glow border
    const borderGeo = new THREE.BoxGeometry(width + 0.3, 0.08, length + 0.3);
    const borderMat = new THREE.MeshStandardMaterial({
      color: 0x6644aa,
      emissive: 0x6644aa,
      emissiveIntensity: 0.6,
      transparent: true,
      opacity: 0.5,
      roughness: 0.3,
      metalness: 0.0,
    });
    const border = new THREE.Mesh(borderGeo, borderMat);
    border.position.y = 0.01;
    group.add(border);
  }

  /** Ice World ramp: light blue translucent wedge */
  private buildIceRamp(
    group: THREE.Group,
    width: number,
    length: number,
    height: number,
    _index: number
  ): void {
    const wedgeGeo = this.buildRampWedge(width, length, height);
    const wedgeMat = new THREE.MeshStandardMaterial({
      color: 0x88ccee,
      transparent: true,
      opacity: 0.75,
      roughness: 0.1,
      metalness: 0.3,
    });
    const wedge = new THREE.Mesh(wedgeGeo, wedgeMat);
    wedge.position.y = 0.02;
    wedge.castShadow = false;
    wedge.receiveShadow = true;
    group.add(wedge);

    // Frost texture overlay (slightly larger translucent box)
    const frostGeo = new THREE.BoxGeometry(width + 0.2, 0.06, length + 0.2);
    const frostMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.3,
      roughness: 0.2,
      metalness: 0.0,
    });
    const frost = new THREE.Mesh(frostGeo, frostMat);
    frost.position.y = 0.01;
    group.add(frost);
  }

  // ────────────────────────────────────────────────────────────
  // Hazard Meshes
  // ────────────────────────────────────────────────────────────

  private buildHazardMeshes(path: TrackPoint[], mapDef: MapDef): void {
    if (!mapDef.hazards || mapDef.hazards.length === 0) return;

    const n = path.length;
    const halfWidth = mapDef.roadWidth / 2;

    for (const def of mapDef.hazards) {
      const pathIdx = Math.floor(def.pathFraction * n) % n;
      const p = path[pathIdx];
      const lateralPos = def.lateralOffset * halfWidth * 0.8;
      const basePosition = new THREE.Vector3(
        p.x + p.nx * lateralPos,
        0,
        p.z + p.nz * lateralPos
      );
      const tangent = new THREE.Vector3(p.dx, 0, p.dz);
      const normal = new THREE.Vector3(p.nx, 0, p.nz);

      const group = new THREE.Group();
      let beam: THREE.Mesh | undefined;

      switch (def.type) {
        case 'boulder': {
          const boulderGeo = new THREE.SphereGeometry(2, 12, 10);
          const boulderMat = new THREE.MeshStandardMaterial({
            color: 0x8b6a4a,
            roughness: 0.9,
            metalness: 0.0,
          });
          const boulder = new THREE.Mesh(boulderGeo, boulderMat);
          boulder.position.y = 2;
          boulder.castShadow = false;
          group.add(boulder);
          break;
        }

        case 'laser_gate': {
          const postMat = new THREE.MeshStandardMaterial({
            color: 0x444466,
            roughness: 0.4,
            metalness: 0.6,
          });

          // Left post
          const postGeo = new THREE.CylinderGeometry(0.25, 0.25, 5, 8);
          const leftPost = new THREE.Mesh(postGeo, postMat);
          leftPost.position.set(-halfWidth * 0.4, 2.5, 0);
          group.add(leftPost);

          // Right post
          const rightPost = new THREE.Mesh(postGeo, postMat);
          rightPost.position.set(halfWidth * 0.4, 2.5, 0);
          group.add(rightPost);

          // Laser beam (flat red emissive plane between posts)
          const beamWidth = halfWidth * 0.8;
          const beamGeo = new THREE.PlaneGeometry(beamWidth, 4);
          const beamMat = new THREE.MeshStandardMaterial({
            color: 0xff0000,
            emissive: 0xff0000,
            emissiveIntensity: 2.0,
            transparent: true,
            opacity: 0.6,
            side: THREE.DoubleSide,
            roughness: 0.1,
            metalness: 0.0,
          });
          beam = new THREE.Mesh(beamGeo, beamMat);
          beam.position.y = 2.5;
          beam.rotation.y = Math.PI / 2;
          group.add(beam);
          break;
        }

        case 'ghost': {
          const ghostGeo = new THREE.SphereGeometry(1.8, 12, 10);
          const ghostMat = new THREE.MeshStandardMaterial({
            color: 0xeeeeff,
            emissive: 0x6666aa,
            emissiveIntensity: 0.8,
            transparent: true,
            opacity: 0.5,
            roughness: 0.3,
            metalness: 0.0,
          });
          const ghost = new THREE.Mesh(ghostGeo, ghostMat);
          ghost.position.y = 2;
          ghost.castShadow = false;
          group.add(ghost);
          break;
        }

        case 'ice_crack': {
          const crackGeo = new THREE.CircleGeometry(3, 16);
          crackGeo.rotateX(-Math.PI / 2);
          const crackMat = new THREE.MeshStandardMaterial({
            color: 0xbbddff,
            emissive: 0x4488aa,
            emissiveIntensity: 0.4,
            transparent: true,
            opacity: 0.6,
            roughness: 0.1,
            metalness: 0.2,
          });
          const crack = new THREE.Mesh(crackGeo, crackMat);
          crack.position.y = 0.03;
          group.add(crack);
          break;
        }
      }

      // Orient the group to face along the track direction
      const angle = Math.atan2(p.dz, p.dx);
      group.position.copy(basePosition);
      group.rotation.y = -angle;

      this.scene.add(group);

      this.hazardEntries.push({
        def,
        group,
        beam,
        basePosition: basePosition.clone(),
        tangent: tangent.clone(),
        normal: normal.clone(),
        halfWidth,
      });
    }
  }

  /** Update hazard mesh positions/visibility from server hazard state */
  updateHazards(hazardStates: HazardState[]): void {
    for (let i = 0; i < this.hazardEntries.length && i < hazardStates.length; i++) {
      const entry = this.hazardEntries[i];
      const state = hazardStates[i];

      // Update position
      entry.group.position.set(state.x, entry.group.position.y, state.z);

      // Update active / visibility
      entry.group.visible = true;
      if (entry.beam) {
        entry.beam.visible = state.active;
      }
    }
  }

  // ────────────────────────────────────────────────────────────
  // Animation — call every frame with elapsed time in seconds
  // ────────────────────────────────────────────────────────────

  animate(time: number): void {
    type ShaderMat = THREE.MeshStandardMaterial & { _shader?: CompiledShader };

    // Road: all per-map variants store their compiled shader here
    if (this.roadMat) {
      const shader = (this.roadMat as ShaderMat)._shader;
      if (shader) shader.uniforms.uTime.value = time;
    }

    // Space station wall panels
    for (const wallMat of this.spaceWallMats) {
      const shader = (wallMat as ShaderMat)._shader;
      if (shader) shader.uniforms.uTime.value = time;
    }

    // Ground (starfield / desert shimmer)
    if (this.groundMat) {
      const shader = (this.groundMat as ShaderMat)._shader;
      if (shader) shader.uniforms.uTime.value = time;
    }
  }

  // ────────────────────────────────────────────────────────────
  // Cleanup
  // ────────────────────────────────────────────────────────────

  clear(): void {
    // Reset animated material references
    this.roadMat = null;
    this.spaceWallMats = [];
    this.groundMat = null;

    // Road
    if (this.roadMesh) {
      this.scene.remove(this.roadMesh);
      this.roadMesh.geometry.dispose();
      (this.roadMesh.material as THREE.Material).dispose();
      this.roadMesh = null;
    }

    // Stripe
    if (this.stripeMesh) {
      this.scene.remove(this.stripeMesh);
      this.stripeMesh.geometry.dispose();
      (this.stripeMesh.material as THREE.Material).dispose();
      this.stripeMesh = null;
    }

    // Walls
    for (const wall of this.wallMeshes) {
      this.scene.remove(wall);
      wall.geometry.dispose();
      (wall.material as THREE.Material).dispose();
    }
    this.wallMeshes = [];

    // Ground
    if (this.groundMesh) {
      this.scene.remove(this.groundMesh);
      this.groundMesh.geometry.dispose();
      (this.groundMesh.material as THREE.Material).dispose();
      this.groundMesh = null;
    }

    // Start line
    if (this.startLineMesh) {
      this.disposeGroup(this.startLineMesh);
      this.scene.remove(this.startLineMesh);
      this.startLineMesh = null;
    }

    // Decorations
    for (const dec of this.decorations) {
      this.scene.remove(dec);
      this.disposeObject(dec);
    }
    this.decorations = [];
    this.tileGroup = null;

    // Hazards
    for (const entry of this.hazardEntries) {
      this.scene.remove(entry.group);
      this.disposeGroup(entry.group);
    }
    this.hazardEntries = [];
  }

  private disposeGroup(group: THREE.Group): void {
    group.traverse((child) => {
      this.disposeObject(child);
    });
  }

  private disposeObject(obj: THREE.Object3D): void {
    if (obj instanceof THREE.Mesh) {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material.dispose();
        }
      }
    }
  }
}
